/**
 * Shared surfaces.
 *
 * `Card` supplies content padding; `Glass` is unpadded for bars and sheets. Their
 * established names are kept for consumers, but they render as solid semantic
 * surfaces. `Inset` is the quieter child surface for dense content.
 */

import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

type DivProps = HTMLAttributes<HTMLDivElement>

export interface SurfaceProps extends DivProps {
  children?: ReactNode
  /** Use only for a card that is genuinely clickable; it brightens rather than
   *  lifting, because a card that lifts reads as a button. */
  interactive?: boolean
  /** A subject or status colour, drawn as a 3 px strip down the leading edge.
   *  Always paired with a label in the content — it identifies, it never carries
   *  meaning on its own. */
  accent?: string
}

/*
 * A card can be tinted by its subject or its result. This used to be a 3px
 * strip down the left edge, which is not an idiom Apple uses anywhere — and
 * once the card lost its outline the strip read as a stray rule rather than as
 * part of the card.
 *
 * Instead the accent tints the surface itself: the card's own fill, mixed with
 * a little of the accent. It is the same signal, carried by the whole shape
 * rather than by an edge, and it survives the rounded corners.
 */
function surfaceStyle(accent?: string, style?: CSSProperties): CSSProperties | undefined {
  if (!accent) return style
  return {
    ...style,
    background: `color-mix(in srgb, ${accent} 12%, var(--glass-fill-strong))`,
  }
}

export function Card({ children, className, interactive, accent, style, ...rest }: SurfaceProps) {
  return (
    <div
      className={['mx-card', interactive && 'mx-glass-interactive', className]
        .filter(Boolean)
        .join(' ')}
      style={surfaceStyle(accent, style)}
      {...rest}
    >
      {children}
    </div>
  )
}

export function Glass({ children, className, interactive, style, ...rest }: SurfaceProps) {
  return (
    <div
      className={['mx-glass', interactive && 'mx-glass-interactive', className]
        .filter(Boolean)
        .join(' ')}
      style={style}
      {...rest}
    >
      {children}
    </div>
  )
}

/** Bars and sheets use the stronger surface fill for stable legibility. */
export function GlassBar({ children, className, style, ...rest }: SurfaceProps) {
  return (
    <div className={['mx-glass', 'mx-glass-bar', className].filter(Boolean).join(' ')} style={style} {...rest}>
      {children}
    </div>
  )
}

/** A quieter child surface. */
export function Inset({ children, className, style, ...rest }: DivProps) {
  return (
    <div className={['mx-inset', className].filter(Boolean).join(' ')} style={style} {...rest}>
      {children}
    </div>
  )
}

export function Divider({ className, ...rest }: HTMLAttributes<HTMLHRElement>) {
  return <hr className={['mx-divider', className].filter(Boolean).join(' ')} {...rest} />
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle?: string
  action?: ReactNode
}) {
  return (
    <header className="mx-page-header">
      <div>
        <h1 className="mx-page-title">{title}</h1>
        {subtitle && <p className="mx-page-subtitle">{subtitle}</p>}
      </div>
      {action}
    </header>
  )
}

export function SectionTitle({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle?: string
  action?: ReactNode
}) {
  return (
    <div className="mx-page-header" style={{ marginBottom: 'var(--space-s)' }}>
      <div>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-lg)',
            fontWeight: 600,
            letterSpacing: 'var(--tracking-tight)',
            margin: 0,
          }}
        >
          {title}
        </h2>
        {subtitle && <p className="mx-page-subtitle">{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}

export function Label({ children, className, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={['mx-label', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </span>
  )
}
